<template>
    <div class="sticky top-0 z-20 -mx-5 border-b border-brand-blue-gray bg-white px-5 py-3 sm:static sm:mx-0 sm:flex sm:items-end sm:justify-between sm:gap-6 sm:px-0 sm:pt-14 sm:pb-0">
        <div role="tablist" :aria-label="labels.stateTabs" class="-mx-5 flex justify-center gap-7 border-b border-brand-blue-gray px-5 sm:mx-0 sm:justify-start sm:gap-9 sm:border-b-0 sm:px-0">
            <button v-for="(state, index) in states"
                    :key="state.abbreviation"
                    :ref="element => tabElements[index] = element"
                    type="button"
                    role="tab"
                    :id="`directory-tab-${state.abbreviation}`"
                    :aria-controls="`directory-panel-${state.abbreviation}`"
                    :aria-selected="isActive(state) ? 'true' : 'false'"
                    :tabindex="isActive(state) ? 0 : -1"
                    class="-mb-px cursor-pointer border-b-[3px] border-transparent pb-3 font-access-legal text-[23px]/[1.2] tracking-tight text-brand-blue-gray-mid transition-colors sm:text-2xl aria-selected:border-brand-lime aria-selected:text-brand-teal"
                    @click="showState(state)"
                    @keydown.left.prevent="showNeighbourState(-1)"
                    @keydown.right.prevent="showNeighbourState(1)">{{ state.name }}</button>
        </div>

        <div class="mt-3 sm:mt-0 sm:pb-3">
            <div class="grid grid-cols-2 gap-2 sm:gap-3">
                <DirectoryFilterSelect :selected="selectedCounty"
                                       :label="labels.regionsServed"
                                       :placeholder="labels.allRegions"
                                       :options="activeState.regions"
                                       @select="selectedCounty = $event" />

                <DirectoryFilterSelect :selected="selectedArea"
                                       :label="labels.practiceAreas"
                                       :placeholder="labels.allPracticeAreas"
                                       :options="activeState.areas"
                                       @select="selectedArea = $event" />
            </div>
        </div>
    </div>

    <div v-for="state in states"
         :key="state.abbreviation"
         :id="`directory-panel-${state.abbreviation}`"
         role="tabpanel"
         :aria-labelledby="`directory-tab-${state.abbreviation}`"
         tabindex="0"
         class="pt-8 sm:pt-9"
         :class="{ hidden: ! isActive(state) }">
        <p v-if="state.attorneys.length === 0" class="text-center text-[13.5px]/[1.5] font-light text-neutral-600 sm:text-base sm:leading-relaxed">{{ state.emptyMessage }}</p>

        <div v-else class="grid grid-cols-1 gap-4 sm:gap-7 lg:grid-cols-2">
            <DirectoryAttorneyCard v-for="attorney in visibleAttorneysByState[state.slug]"
                                   :key="attorney.uuid"
                                   :attorney="attorney"
                                   :labels="labels"
                                   @connect="connectTo" />
        </div>
    </div>

    <div :class="{ hidden: ! hasNoResults }" aria-live="polite">
        <div class="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-brand-blue-gray-mid px-6 py-10 text-center sm:mt-9 sm:gap-4 sm:px-8 sm:py-16">
            <p class="font-access-legal text-2xl/[1.2] text-brand-teal sm:text-3xl/[1.2]">{{ labels.noResults.heading }}</p>

            <p class="max-w-md text-[13.5px]/[1.6] font-light text-neutral-600 sm:text-base sm:leading-relaxed">{{ labels.noResults.description }}</p>

            <button type="button" class="mt-1 cursor-pointer rounded-full border border-brand-teal px-6 py-3 text-[13.5px]/5 font-medium text-brand-teal transition-colors hover:bg-brand-teal hover:text-white sm:text-base" @click="clearFilters">
                {{ labels.noResults.clear }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import DirectoryAttorneyCard from './DirectoryAttorneyCard.vue';
import DirectoryFilterSelect from './DirectoryFilterSelect.vue';
import preventIntakeSelfScroll from './preventIntakeSelfScroll';

const store = useStore();

const props = defineProps({
    states: {
        type: Array,
        required: true,
    },
    labels: {
        type: Object,
        required: true,
    },
});

const stateBySlug = (slug) => props.states.find((state) => state.slug === slug);
const slugInLocationHash = () => window.location.hash.replace('#', '').toLowerCase();
const requestedSlug = () => (stateBySlug(slugInLocationHash()) ?? props.states[0]).slug;

const activeSlug = ref(requestedSlug());
const activeState = computed(() => stateBySlug(activeSlug.value));
const isActive = (state) => state.slug === activeSlug.value;

const showState = (state) => {
    activeSlug.value = state.slug;
};

const showStateInLocationHash = () => {
    const requested = stateBySlug(slugInLocationHash());

    if (requested) {
        showState(requested);
    }
};

const tabElements = [];

const showNeighbourState = (step) => {
    const neighbour = (props.states.indexOf(activeState.value) + step + props.states.length) % props.states.length;

    showState(props.states[neighbour]);
    tabElements[neighbour].focus();
};

const selectedCounty = ref('');
const selectedArea = ref('');

const clearFilters = () => {
    selectedCounty.value = '';
    selectedArea.value = '';
};

const servesSelectedCounty = (attorney) => selectedCounty.value === ''
    || attorney.countiesWildcard
    || attorney.counties.includes(selectedCounty.value);

const practicesSelectedArea = (attorney) => selectedArea.value === ''
    || attorney.areas.includes(selectedArea.value);

const matchesFilters = (attorney) => servesSelectedCounty(attorney) && practicesSelectedArea(attorney);

const visibleAttorneysByState = computed(() => Object.fromEntries(
    props.states.map((state) => [state.slug, state.attorneys.filter(matchesFilters)])
));

const visibleAttorneys = computed(() => visibleAttorneysByState.value[activeSlug.value]);
const hasNoResults = computed(() => activeState.value.attorneys.length > 0 && visibleAttorneys.value.length === 0);

watch(activeSlug, (slug) => {
    clearFilters();
    window.history.replaceState(null, '', `#${slug}`);
});

const restoreNativeScrollBy = preventIntakeSelfScroll();

onMounted(() => window.addEventListener('hashchange', showStateInLocationHash));
onBeforeUnmount(() => {
    window.removeEventListener('hashchange', showStateInLocationHash);
    restoreNativeScrollBy();
});

const focusIntakeInput = (remainingFrames) => {
    if (window.innerWidth < 640) {
        return;
    }

    const input = document.querySelector('#form input:not([type="hidden"]), #form textarea');

    if (input) {
        input.focus({ preventScroll: true });
        return;
    }

    if (remainingFrames <= 0) {
        return;
    }

    window.requestAnimationFrame(() => focusIntakeInput(remainingFrames - 1));
};

const personalizeIntakeHeading = (name) => {
    const heading = document.getElementById('connecting-with');

    if (heading) {
        heading.textContent = heading.dataset.template.replace(':name', () => name);
    }
};

/**
 * Naming the firm in the heading and offering them to the intake as the visitor's
 * preferred firm are the same gesture, so they are set together. The store getter
 * discards a listing it cannot send, which leaves the intake on its generic copy
 * rather than promising a firm the preflight was never asked about.
 */
const connectTo = (attorney) => {
    store.commit('lead/preferredTenant', attorney);

    personalizeIntakeHeading(attorney.name);
    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    focusIntakeInput(120);
};
</script>
